import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import api, { formatApiError } from "@/lib/api";
import { readSource } from "@/lib/source";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { btnPrimary, input, money } from "@/components/simple/product/Fields";

export const TERMS = "Full payment before production. Files print as submitted unless you chose cleanup. Customer-supplied apparel is not replaced.";

const toPayload = (items, contact, fulfillment, payNow) => ({
    items: items.map((i) => ({ product_slug: i.product_slug, product_name: i.product_name, selections: i.selections, artwork: i.artwork || [], proof: i.proof || null, summary: i.summary || "" })),
    contact, notes: "", source: readSource(), pay_now: payNow, fulfillment,
});

/** `/checkout/:store` in Simple (7.7): no screen of ours — the order is created and Stripe opens at once. */
export default function SimpleCheckout() {
    const { store: slug } = useParams();
    const { bags, clearStore } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const bag = useMemo(() => bags.find((b) => b.slug === slug), [bags, slug]);
    const fulfillment = location.state?.fulfillment || sessionStorage.getItem("checkout_fulfillment") || "pickup";
    const [shop, setShop] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "" });
    const [agreed, setAgreed] = useState(false);
    const started = useRef(false);

    useEffect(() => {
        if (!slug) return;
        api.get(`/stores/${slug}`).then(({ data }) => setShop(data.store || {})).catch(() => setShop({}));
    }, [slug]);

    const jump = async () => {
        setBusy(true); setError("");
        try {
            const { data } = await api.post("/orders", toPayload(bag.items, {}, fulfillment, true));
            sessionStorage.setItem("pending_pay", JSON.stringify({ slug, order_id: data.id }));
            const origin = window.location.origin;
            const res = await api.post("/payments/order/checkout", { order_id: data.id, origin_url: origin, cancel_url: `${origin}/cart?cancelled=${data.id}` });
            window.location.href = res.data.checkout_url;
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Couldn't open the register. Try again.");
            setBusy(false);
            started.current = false;
        }
    };

    // card register on → straight to Stripe, once
    useEffect(() => {
        if (!bag || !shop || started.current) return;
        if (shop.accepts_cards) { started.current = true; jump(); }
    }, [bag, shop]); // eslint-disable-line react-hooks/exhaustive-deps

    const place = async (e) => {
        e.preventDefault();
        if (!agreed) { setError("Tick the box to continue."); return; }
        setBusy(true); setError("");
        try {
            const { data } = await api.post("/orders", toPayload(bag.items, form, fulfillment, false));
            clearStore(slug);
            navigate(`/order-confirmation/${data.id}`, { replace: true });
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || "Couldn't place the order. Try again.");
            setBusy(false);
        }
    };

    if (!bag) return <Navigate to="/cart" replace />;
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <Layout title="Checkout">
            <section className="mx-auto w-full max-w-4xl px-3 py-4 sm:px-6" data-testid="simple-checkout">
                {(!shop || shop.accepts_cards) ? (
                    <div data-testid="checkout-jump">
                        <h1 className="text-[28px] font-semibold leading-tight">{error ? "The register didn't open." : "Opening secure checkout…"}</h1>
                        <p className="mt-2 text-[16px] text-muted-ink">{money(bag.subtotal)} · {bag.name}</p>
                        {error && (
                            <div className="mt-4" data-testid="checkout-error">
                                <p className="text-[16px]" style={{ color: "var(--error)" }}>{error}</p>
                                <button type="button" onClick={() => { started.current = true; jump(); }} disabled={busy} className={`${btnPrimary} mt-3`} data-testid="checkout-retry">Try again</button>
                                <Link to="/cart" className="mt-3 flex min-h-[44px] items-center text-[16px] underline" data-testid="checkout-back">Back to cart</Link>
                            </div>
                        )}
                    </div>
                ) : (
                    <form onSubmit={place} data-testid="checkout-form">
                        <h1 className="text-[28px] font-semibold leading-tight">Place your order</h1>
                        <p className="mt-2 text-[16px] text-muted-ink">{bag.name} takes payment when the job is ready. {money(bag.subtotal)}.</p>
                        <div className="mt-4 flex flex-col gap-3">
                            <input className={input} placeholder="Name" value={form.name} onChange={set("name")} required autoComplete="name" data-testid="checkout-name" />
                            <input className={input} type="tel" placeholder="Mobile phone" value={form.phone} onChange={set("phone")} required autoComplete="tel" data-testid="checkout-phone" />
                            <input className={input} type="email" placeholder="Email" value={form.email} onChange={set("email")} required autoComplete="email" data-testid="checkout-email" />
                        </div>
                        <label className="mt-4 flex items-start gap-3 text-[16px]">
                            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-5 w-5" data-testid="checkout-terms" />
                            <span data-testid="checkout-terms-text">{TERMS}</span>
                        </label>
                        {error && <p className="mt-3 text-[16px]" style={{ color: "var(--error)" }} data-testid="checkout-error">{error}</p>}
                        <button type="submit" disabled={busy} className={`${btnPrimary} mt-4`} data-testid="checkout-place">{busy ? "Working…" : "Place order"}</button>
                        <Link to="/cart" className="mt-3 flex min-h-[44px] items-center text-[16px] underline" data-testid="checkout-back">Back to cart</Link>
                    </form>
                )}
            </section>
        </Layout>
    );
}
